import {
    AddRounded,
    CloseFullscreenRounded,
    ContentCopyRounded,
    DeleteRounded,
    EditRounded,
    ImageRounded,
    OpenInFullRounded,
    ChatBubbleOutlineRounded,
    FormatQuoteRounded,
    FormatColorFillRounded,
    FormatColorTextRounded,
    ViewModuleRounded,
    GridViewRounded,
    ViewAgendaRounded,
    ViewCompactRounded,
    HorizontalRuleRounded,
    FormatAlignLeftRounded,
    FormatAlignCenterRounded,
    FormatAlignRightRounded,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Grid,
    IconButton,
    InputBase,
    Popover,
    Stack,
    Tooltip,
    Typography,
    alpha,
    useTheme,
    Card,
    CardMedia,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
    Slider,
} from "@mui/material";
import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { reduxHelper } from "../../../config/reduxHelper";
import { customComponentTypes } from "../../../customComponents/types";
import CommonCardGrid from "./CommonCardGrid";
import CommonStatGrid from "./CommonStatGrid";
import CommonTestimonialGrid from "./CommonTestimonialGrid";
import TableBlock from "./TableBlock";
import Iconify from "../../Iconify";
import FloatingTextToolbar from "../../siteBuilder/properties/FloatingTextToolbar";
import TextPropertiesPanel from "../../siteBuilder/properties/TextPropertiesPanel";
import FlexibleEditor from "./FlexibleEditor";
import CustomComponent from "../../../customComponents/CustomComponent";
import FlexibleButtonBlock from "./FlexibleButtonBlock";
import FlexibleGenericCardBlock from "./FlexibleGenericCardBlock";
import FlexibleContentBlock from "./FlexibleContentBlock";
import FlexibleFeatureCardBlock from "./FlexibleFeatureCardBlock";
import FlexibleSpacerBlock from "./FlexibleSpacerBlock";
import FlexibleIconBlock from "./FlexibleIconBlock";
import FlexibleDropdownBlock from "./FlexibleDropdownBlock";
import FlexibleFormBlock from "./FlexibleFormBlock";


const formatClassicQuoteHtml = (html = "") => {
    if (!html) return html;
    // Already formatted with class names — return as-is
    if (/class="quote-title"/i.test(html)) return html;
    // Old format: divs with inline font-size styles — convert to class-based
    if (/<div[^>]+style=/i.test(html)) {
        const matches = [...html.matchAll(/<div[^>]*>([\s\S]*?)<\/div>/gi)];
        if (matches.length >= 2) {
            const [first, ...rest] = matches;
            return `<div class="quote-title">${first[1]}</div><div class="quote-description">${rest.map((m) => m[1]).join("<br/>")}</div>`;
        }
        return html;
    }
    // Already contains other block-level elements — leave untouched
    if (/<(div|p|h1|h2|h3|h4|h5|h6|blockquote)\b/i.test(html)) return html;
    // Plain text separated by <br/> — wrap first line as title, rest as description
    const parts = html
        .split(/<br\s*\/?>/i)
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length < 2) return html;
    const [title, ...rest] = parts;
    return `<div class="quote-title">${title}</div><div class="quote-description">${rest.join("<br/>")}</div>`;
};

// Module-level cache — persists expanded state across CommonBlock remounts (same pattern as TeamCategoryBlock1)
const _accordionExpandedCache = {};

// ── Image block with inline toolbar + drag-to-resize ──────────────────────────
const ImageBlock = ({ block, isSiteBuilder, isActive, onOpenGallery, handleUpdate, theme }) => {
    const savedWidth = block.imgWidth || 100;
    const imgAlign = block.imgAlign || 'center';
    const borderRadius = block.borderRadius ?? 8;
    const [liveRadius, setLiveRadius] = useState(borderRadius);
    useEffect(() => { setLiveRadius(borderRadius); }, [borderRadius]);

    const [liveWidth, setLiveWidth] = useState(savedWidth);
    useEffect(() => { setLiveWidth(savedWidth); }, [savedWidth]);


    const containerRef = useRef(null);
    const dragStateRef = useRef(null); // { startX, startW, containerW, dir }

    const alignJustify = imgAlign === 'left' ? 'flex-start' : imgAlign === 'right' ? 'flex-end' : 'center';

    // Attach window-level listeners once per drag gesture using refs → no stale closure issues
    useEffect(() => {
        const onMove = (ev) => {
            const ds = dragStateRef.current;
            if (!ds) return;
            const rawDelta = ((ev.clientX - ds.startX) / ds.containerW) * 100;
            const delta = ds.dir === 'left' ? -rawDelta : rawDelta;
            const next = Math.min(100, Math.max(10, Math.round(ds.startW + delta)));
            setLiveWidth(next);
        };
        const onUp = () => {
            if (!dragStateRef.current) return;
            // Read the live width from DOM-driven state via a stable ref
            setLiveWidth((w) => {
                handleUpdate({ imgWidth: w });
                return w;
            });
            dragStateRef.current = null;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [handleUpdate]);

    const startDrag = (e, dir) => {
        e.preventDefault();
        e.stopPropagation();
        dragStateRef.current = {
            startX: e.clientX,
            startW: liveWidth,
            containerW: containerRef.current?.getBoundingClientRect().width || 800,
            dir,
        };
    };

    return (
        <Box ref={containerRef} sx={{ position: 'relative', my: 1 }}>

            {/* ── Toolbar ─────────────────────────────────────────────────── */}
            {isSiteBuilder && isActive && (
                <Stack direction="row" spacing={0.5} alignItems="center"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute', top: -44, left: '50%', transform: 'translateX(-50%)',
                        zIndex: 1000, p: 0.5, bgcolor: 'background.paper',
                        boxShadow: 20, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Tooltip title="Change image">
                        <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onOpenGallery(block.id, null, e.currentTarget.getBoundingClientRect()); }}>
                            <ImageRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>

                    <Divider orientation="vertical" flexItem />

                    {[
                        { val: 'left', icon: <FormatAlignLeftRounded sx={{ fontSize: 16 }} /> },
                        { val: 'center', icon: <FormatAlignCenterRounded sx={{ fontSize: 16 }} /> },
                        { val: 'right', icon: <FormatAlignRightRounded sx={{ fontSize: 16 }} /> },
                    ].map(a => (
                        <Tooltip key={a.val} title={`Align ${a.val}`}>
                            <IconButton size="small"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdate({ imgAlign: a.val }); }}
                                sx={{ borderRadius: 0.5, bgcolor: imgAlign === a.val ? alpha(theme.palette.primary.main, 0.12) : 'transparent', color: imgAlign === a.val ? 'primary.main' : 'inherit' }}>
                                {a.icon}
                            </IconButton>
                        </Tooltip>
                    ))}

                    <Divider orientation="vertical" flexItem />

                    {/* Border radius inline slider */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}
                        onMouseDown={(e) => e.stopPropagation()}>
                        <Slider
                            size="small"
                            min={0} max={48} step={1}
                            value={liveRadius}
                            onChange={(_, v) => setLiveRadius(v)}
                            onChangeCommitted={(_, v) => handleUpdate({ borderRadius: v })}
                            sx={{ width: 80, color: 'primary.main', py: '6px' }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 26 }}>{liveRadius}px</Typography>
                    </Stack>
                </Stack>
            )}

            {/* ── Image ───────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: alignJustify }}>
                <Box sx={{
                    position: 'relative',
                    width: `${liveWidth}%`,
                    userSelect: 'none',
                    '&:hover .img-resize-handle': isSiteBuilder ? { opacity: 1 } : {},
                }}>
                    {block.value ? (
                        <Box component="img" src={block.value} alt="image"
                            sx={{ display: 'block', width: '100%', borderRadius: `${liveRadius}px`, objectFit: 'contain', pointerEvents: 'none' }} />
                    ) : isSiteBuilder ? (
                        <Box onClick={() => {
                            if (!isActive) {
                                // Let it bubble up to FlexibleEditor to select the block
                            } else {
                                onOpenGallery(block.id);
                            }
                        }}
                            sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'action.hover', borderRadius: `${liveRadius}px`, border: '2px dashed', borderColor: 'divider', cursor: 'pointer' }}>
                            <ImageRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">Click to add image</Typography>
                        </Box>
                    ) : null}

                    {/* Change image overlay */}
                    {isSiteBuilder && block.value && (
                        <Box onClick={(e) => {
                            if (!isActive) {
                                // Allow bubbling to select
                            } else {
                                e.stopPropagation();
                                onOpenGallery(block.id);
                            }
                        }}
                            sx={{
                                position: 'absolute', inset: 0, borderRadius: `${liveRadius}px`,
                                bgcolor: alpha('#000', 0.4), opacity: 0, transition: '0.2s',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                '&:hover': { opacity: 1 },
                            }}>
                            <Button variant="contained" size="small" startIcon={<EditRounded />} sx={{ textTransform: 'none', pointerEvents: 'none' }}>Change Image</Button>
                        </Box>
                    )}

                    {/* Resize handles */}
                    {isSiteBuilder && block.value && (
                        <>
                            <Box className="img-resize-handle" onMouseDown={(e) => startDrag(e, 'right')}
                                sx={{ position: 'absolute', right: -5, top: '50%', transform: 'translateY(-50%)', width: 10, height: 44, borderRadius: 5, bgcolor: 'primary.main', opacity: 0, transition: 'opacity 0.15s', cursor: 'ew-resize', zIndex: 10 }} />
                            <Box className="img-resize-handle" onMouseDown={(e) => startDrag(e, 'left')}
                                sx={{ position: 'absolute', left: -5, top: '50%', transform: 'translateY(-50%)', width: 10, height: 44, borderRadius: 5, bgcolor: 'primary.main', opacity: 0, transition: 'opacity 0.15s', cursor: 'ew-resize', zIndex: 10 }} />
                        </>
                    )}
                </Box>
            </Box>

        </Box>
    );
};

const ButtonBlock = ({ block, isSiteBuilder, isActive, handleUpdate, theme, pageFontSx }) => {
    return (

        <Box
            contentEditable={isSiteBuilder}
            suppressContentEditableWarning
            onBlur={(e) => {
                const val = e.currentTarget.innerText;
                handleUpdate({
                    value: val,
                    label: val,
                    children: val
                });
            }}
            onClick={(e) => { if (isSiteBuilder) e.stopPropagation(); }}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                py: 1.2,
                minWidth: 100,
                borderRadius: block.props?.sx?.borderRadius || '8px',
                bgcolor: (block.props?.sx?.backgroundColor || theme.palette.primary.main),
                color: (block.props?.style?.color || '#fff'),
                borderColor: (block.props?.sx?.borderColor || 'transparent'),
                borderStyle: (block.props?.sx?.borderStyle || 'solid'),
                borderWidth: (block.props?.sx?.borderWidth || 0),
                fontWeight: block.props?.style?.fontWeight || 600,
                fontSize: block.props?.style?.fontSize || '0.9rem',
                textAlign: block.props?.style?.textAlign || 'center',
                cursor: isSiteBuilder ? 'text' : 'pointer',
                userSelect: isSiteBuilder ? 'text' : 'none',
                transition: 'all 0.2s',
                outline: 'none',
                ...pageFontSx,
                '&:hover': {
                    opacity: 0.9,
                    transform: isSiteBuilder ? 'none' : 'translateY(-1px)',
                }
            }}
        >
            {block.value || block.label || 'Button'}
        </Box>
    );
};

const CommonBlock = ({
    block,
    isSiteBuilder = false,
    onUpdate,
    onDelete,
    onDuplicate,
    onSplit,
    onOpenGallery,
    onKeyDown,
    onTriggerPicker,
    isActive = false,
    editingCardId,
    setEditingCardId,
    disableBlockActions = false,
    hideBlockDelete = false,
    nestedDelete = false,
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { reducer } = useSelector((state) => state);
    const pageFontFamily = reducer?.pageJson?.pageStyle?.fontFamily;
    const pageFontSx =
        pageFontFamily && pageFontFamily !== 'inherit'
            ? { fontFamily: `${pageFontFamily} !important` }
            : {};

    const { siteBuilderSelectedChild } = reducer;
    const sel = siteBuilderSelectedChild;
    const selectedChildMatchesBlock =
        (sel?.uid != null && block?.uid != null && sel.uid === block.uid) ||
        (sel?.id != null && block?.id != null && String(sel.id) === String(block.id));
    const isActuallyActive = isActive || (isSiteBuilder && selectedChildMatchesBlock);
    const [expandedAccordion, _setExpandedAccordion] = useState(() => !!_accordionExpandedCache[block.id]);
    const setExpandedAccordion = useCallback((value) => {
        _setExpandedAccordion((prev) => {
            const next = typeof value === 'function' ? value(prev) : value;
            _accordionExpandedCache[block.id] = next;
            return next;
        });
    }, [block.id]);
    const [accordionHiddenPreview, setAccordionHiddenPreview] = useState(false);
    const [selectionRect, setSelectionRect] = useState(null);
    const [lastSelectionRect, setLastSelectionRect] = useState(null);
    const [toolbarLinkMode, setToolbarLinkMode] = useState(false);
    const [toolbarUrl, setToolbarUrl] = useState("");

    useEffect(() => {
        if (selectionRect) setLastSelectionRect(selectionRect);
    }, [selectionRect]);

    const [quoteColorAnchor, setQuoteColorAnchor] = useState(null);
    const [quoteColorTarget, setQuoteColorTarget] = useState(null);
    const containerRef = useRef(null);
    const isMouseOverToolbarRef = useRef(false);
    const toolbarEnabledTypes = [
        'text',
        'cardgrid',
        'table',
        'statgrid',
        'testimonialgrid',
        'flexible-button',
        'flexible-genericCard',
        'flexible-featureCard',
        'flexible-content',
        'flexible-icon',
        'flexible-dropdown',
        'flexible-form',
        'accordion',
        'customcomponent'
    ];
    const toolbarFallbackRect = {
        top: 20,
        left: (containerRef.current?.clientWidth || 0) / 2,
        width: 0,
        height: 20
    };

    const handleMouseUp = (e) => {
        if (!isSiteBuilder || isMouseOverToolbarRef.current) return;
        const eventTarget = e.target;
        const clickedToolbar = eventTarget instanceof Element && eventTarget.closest('[data-floating-text-toolbar="true"]');
        if (clickedToolbar) return;
        if (!toolbarEnabledTypes.includes(block.type)) {
            setSelectionRect(null);
            return;
        }
        const selection = window.getSelection();
        if (selection.rangeCount > 0 && selection.toString().trim() !== "" && e.currentTarget.contains(selection.anchorNode)) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const containerRect = containerRef.current?.getBoundingClientRect();

            if (containerRect) {
                const nextRect = {
                    top: rect.top - containerRect.top,
                    left: rect.left - containerRect.left,
                    width: rect.width,
                    height: rect.height,
                    // Pass absolute top too just in case we need it for fallback
                    absoluteTop: rect.top,
                    absoluteLeft: rect.left,
                    absoluteHeight: rect.height
                };
                // Persist immediately so toolbar doesn't disappear during focus/blur races.
                setSelectionRect(nextRect);
                setLastSelectionRect(nextRect);
            }
        } else {
            // Keep toolbar anchored while opening/using link mode.
            if (toolbarLinkMode) return;
            setSelectionRect(null);
        }
    };


    const handleUpdate = (updates) => {
        if (typeof updates === 'string') {
            onUpdate(block.id, { value: updates });
        } else if (typeof updates === 'function') {
            onUpdate(block.id, updates);
        } else {
            onUpdate(block.id, updates);
        }
    };

    const inlineFormCustomItem = block?.type === 'customcomponent' ? (block.customItem || {}) : {};
    const isFormCustomInline =
        (block.type === 'customcomponent' && (block.variant === 'form' || inlineFormCustomItem?.type === customComponentTypes.form)) ||
        block.type === 'flexible-form';

    const hasCustomToolbar = isFormCustomInline || ['flexible-icon', 'flexible-dropdown'].includes(block.type);

    const openFormEditorFromFlexibleBlock = useCallback(() => {
        dispatch({ type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD, payload: { ...block, showEditor: true } });
        dispatch({ type: reduxHelper.UPDATE_BLOCK_PROPERTIES, payload: true });
    }, [block, dispatch]);

    const openIconEditorFromFlexibleBlock = useCallback(() => {
        dispatch({ type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD, payload: { ...block, showEditor: true } });
        dispatch({ type: reduxHelper.UPDATE_BLOCK_PROPERTIES, payload: true });
    }, [block, dispatch]);

    const openDropdownEditorFromFlexibleBlock = useCallback(() => {
        dispatch({ type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD, payload: { ...block, showEditor: true } });
        dispatch({ type: reduxHelper.UPDATE_BLOCK_PROPERTIES, payload: true });
    }, [block, dispatch]);

    const renderBlockActions = () => (
        isSiteBuilder && !disableBlockActions && !hideBlockDelete && typeof onDelete === 'function' && !hasCustomToolbar && (
            <IconButton
                className="block-actions"
                size="small"
                color="error"
                onClick={() => onDelete(block.id)}
                sx={{
                    position: 'absolute',
                    ...(nestedDelete
                        ? { top: 4, right: 4, left: 'auto' }
                        : { left: 2, top: -10 }
                    ),
                    opacity: isActuallyActive ? 1 : 0,
                    transition: 'opacity 0.2s',
                    zIndex: 30,
                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 1,
                    width: 22,
                    height: 22,
                    p: 0.25,
                    '@media (hover: none), (pointer: coarse)': {
                        opacity: 1,
                    },
                }}
            >
                <DeleteRounded sx={{ fontSize: 14 }} />
            </IconButton>
        )
    );

    const renderContent = () => {
        switch (block.type) {
            case 'image':
                return <ImageBlock block={block} isSiteBuilder={isSiteBuilder} isActive={isActuallyActive} onOpenGallery={onOpenGallery} handleUpdate={handleUpdate} theme={theme} />;

            case 'button':
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <ButtonBlock
                            block={block}
                            isSiteBuilder={isSiteBuilder}
                            isActive={isActuallyActive}
                            handleUpdate={handleUpdate}
                            theme={theme}
                            pageFontSx={pageFontSx}
                        />
                    </Box>
                );

            case 'flexible-button':
                return (
                    <FlexibleButtonBlock
                        block={block}
                        isSiteBuilder={isSiteBuilder}
                        isActive={isActuallyActive}
                        handleUpdate={handleUpdate}
                        pageFontSx={pageFontSx}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                    />
                );

            case 'flexible-genericCard':
                return (
                    <FlexibleGenericCardBlock
                        block={block}
                        isSiteBuilder={isSiteBuilder}
                        isActive={isActuallyActive}
                        handleUpdate={handleUpdate}
                        pageFontSx={pageFontSx}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                    />
                );

            case 'flexible-content':
                return (
                    <FlexibleContentBlock
                        block={block}
                        isSiteBuilder={isSiteBuilder}
                        isActive={isActuallyActive}
                        handleUpdate={handleUpdate}
                        pageFontSx={pageFontSx}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                    />
                );

            case 'flexible-featureCard':
                return (
                    <FlexibleFeatureCardBlock
                        block={block}
                        isSiteBuilder={isSiteBuilder}
                        isActive={isActuallyActive}
                        handleUpdate={handleUpdate}
                        pageFontSx={pageFontSx}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                    />
                );

            case 'flexible-spacer':
                return (
                    <FlexibleSpacerBlock
                        block={block}
                        isSiteBuilder={isSiteBuilder}
                        isActive={isActuallyActive}
                        handleUpdate={handleUpdate}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                    />
                );

            case 'flexible-icon':
                return (
                    <Box sx={{ position: 'relative' }}>
                        {isSiteBuilder && isActuallyActive && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    position: 'absolute',
                                    top: -44,
                                    right: 0,
                                    zIndex: 10,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 0.5,
                                    boxShadow: 1,
                                }}
                            >
                                <Tooltip title="Edit icon">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openIconEditorFromFlexibleBlock();
                                        }}
                                    >
                                        <EditRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                {typeof onDuplicate === 'function' && (
                                    <Tooltip title="Duplicate">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDuplicate(block.id);
                                            }}
                                        >
                                            <ContentCopyRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {typeof onDelete === 'function' && (
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(block.id);
                                            }}
                                        >
                                            <DeleteRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        )}
                        <FlexibleIconBlock
                            block={block}
                            isSiteBuilder={isSiteBuilder}
                            onUpdate={handleUpdate}
                        />
                    </Box>
                );

            case 'flexible-dropdown':
                return (
                    <Box sx={{ position: 'relative' }}>
                        {isSiteBuilder && isActuallyActive && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    position: 'absolute',
                                    top: -44,
                                    right: 0,
                                    zIndex: 10,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 0.5,
                                    boxShadow: 1,
                                }}
                            >
                                <Tooltip title="Edit dropdown">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openDropdownEditorFromFlexibleBlock();
                                        }}
                                    >
                                        <EditRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                {typeof onDuplicate === 'function' && (
                                    <Tooltip title="Duplicate">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDuplicate(block.id);
                                            }}
                                        >
                                            <ContentCopyRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {typeof onDelete === 'function' && (
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(block.id);
                                            }}
                                        >
                                            <DeleteRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        )}
                        <FlexibleDropdownBlock
                            block={block}
                            isSiteBuilder={isSiteBuilder}
                            onUpdate={handleUpdate}
                        />
                    </Box>
                );

            case 'flexible-form':
                return (
                    <Box sx={{ position: 'relative' }}>
                        {isSiteBuilder && isActuallyActive && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    position: 'absolute',
                                    top: -44,
                                    right: 0,
                                    zIndex: 10,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 0.5,
                                    boxShadow: 1,
                                }}
                            >
                                <Tooltip title="Edit form">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openFormEditorFromFlexibleBlock();
                                        }}
                                    >
                                        <EditRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                {typeof onDuplicate === 'function' && (
                                    <Tooltip title="Duplicate">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDuplicate(block.id);
                                            }}
                                        >
                                            <ContentCopyRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {typeof onDelete === 'function' && (
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(block.id);
                                            }}
                                        >
                                            <DeleteRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        )}
                        <FlexibleFormBlock
                            block={block}
                            isSiteBuilder={isSiteBuilder}
                            onUpdate={handleUpdate}
                        />
                    </Box>
                );

            case 'table':
                return (
                    <TableBlock
                        data={block.data}
                        props={block.props}
                        isEditable={isSiteBuilder}
                        onChange={(newData) => handleUpdate({ data: newData })}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                    />
                );

            case 'cardgrid': {
                const variant = block.variant || 'grid-3';
                const cardGridLikeVariants = ['grid-2', 'grid-3', 'compact', 'image-showcase', 'featured-block'];
                return (
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        border: (isSiteBuilder && isActive) ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                        borderRadius: 2,
                        p: (isSiteBuilder && isActive) ? 1 : 0,
                        transition: 'all 0.2s'
                    }}>
                        {isSiteBuilder && !disableBlockActions && (
                            <Stack direction="row" spacing={1} sx={{
                                position: 'absolute', top: -45, right: 0, zIndex: 10,
                                bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                                borderRadius: 1, p: 0.5, boxShadow: 1,
                                opacity: (isActive && !editingCardId) ? 1 : 0, transition: 'opacity 0.2s',
                                '.relative:hover &': { opacity: (isActive && !editingCardId) ? 1 : 0 }
                            }} className="grid-variant-toolbar">
                                <IconButton size="small" onClick={() => onDelete(block.id)} color="error">
                                    <DeleteRounded fontSize="small" />
                                </IconButton>
                            </Stack>
                        )}
                        <CommonCardGrid
                            cards={block.cards || []}
                            variant={variant}
                            lastGridVariant={block.lastGridVariant}
                            isSiteBuilder={isSiteBuilder}
                            onUpdateCards={(cardsOrUpdater) => {
                                handleUpdate((prevBlock) => {
                                    const prevCards = prevBlock.cards || [];
                                    const nextCards = typeof cardsOrUpdater === 'function'
                                        ? cardsOrUpdater(prevCards)
                                        : cardsOrUpdater;
                                    return { cards: nextCards };
                                });
                            }}
                            onDeleteCard={(cardId) => {
                                const newCards = (block.cards || []).filter(c => String(c.id) !== String(cardId));
                                handleUpdate({ cards: newCards });
                            }}
                            onOpenGallery={(cardId) => {
                                setEditingCardId(String(cardId));
                                onOpenGallery(block.id, cardId);
                            }}
                            onUpdateVariant={(newVariant) => {
                                handleUpdate((prevBlock) => {
                                    const prevV = prevBlock.variant || 'grid-3';
                                    const next = { variant: newVariant };
                                    if (
                                        cardGridLikeVariants.includes(prevV) &&
                                        (newVariant === 'list' || newVariant === 'feature')
                                    ) {
                                        next.lastGridVariant = prevV;
                                    }
                                    return next;
                                });
                            }}
                            onTriggerPicker={onTriggerPicker}
                            blockId={block.id}
                            editingCardId={editingCardId}
                            setEditingCardId={setEditingCardId}
                        />
                    </Box>
                );
            }

            case 'statgrid':
                return (
                    <CommonStatGrid
                        stats={block.stats || []}
                        displayType={block.displayType || 'number'}
                        statCols={block.statCols || 2}
                        accentColor={block.accentColor}
                        isSiteBuilder={isSiteBuilder}
                        isActive={isActive}
                        onUpdateStats={(updated) => handleUpdate({ stats: updated })}
                        onUpdateBlock={(updates) => handleUpdate(updates)}
                    />
                );

            case 'testimonialgrid':
                return (
                    <CommonTestimonialGrid
                        items={block.items || []}
                        layout={block.layout || 'card'}
                        cols={block.cols || 2}
                        showImage={block.showImage !== false}
                        showRating={block.showRating !== false}
                        isScroll={block.isScroll}
                        isAutoplay={block.isAutoplay}
                        accentColor={block.accentColor}
                        isSiteBuilder={isSiteBuilder}
                        isActive={isActive}
                        onUpdateItems={(updated) => handleUpdate({ items: updated })}
                        onUpdateBlock={(updates) => handleUpdate(updates)}
                        onOpenGallery={(itemId) => {
                            setEditingCardId(String(itemId));
                            onOpenGallery(block.id, itemId);
                        }}
                    />
                );

            case 'divider':
                return <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.4), borderBottomWidth: 1.5, my: 2 }} />;

            case 'columns': {
                const numCols = block.numCols || 2;
                const colDefs = block.columns || Array.from({ length: numCols }, (_, i) => ({ id: Date.now() + i, blocks: [] }));
                const colWidth = Math.floor(12 / numCols);
                const colGap = block.colGap ?? 2;

                const updateColumn = (colId, updatedBlocks) => {
                    handleUpdate({
                        columns: colDefs.map(c => String(c.id) === String(colId) ? { ...c, blocks: updatedBlocks } : c)
                    });
                };

                const applyNumCols = (n) => {
                    let next = [...colDefs];
                    while (next.length < n) next.push({ id: Date.now() + next.length, blocks: [] });
                    while (next.length > n) next.pop();
                    handleUpdate({ numCols: n, columns: next });
                };

                return (
                    <Box sx={{ position: 'relative', my: 1 }}>
                        {/* Toolbar */}
                        {isSiteBuilder && isActive && (
                            <Stack direction="row" spacing={0.5} alignItems="center"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    position: 'absolute', top: -44, left: '50%', transform: 'translateX(-50%)',
                                    zIndex: 1000, p: 0.5, bgcolor: 'background.paper',
                                    boxShadow: 20, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {[1, 2, 3, 4].map(n => (
                                    <Tooltip key={n} title={`${n} column${n > 1 ? 's' : ''}`}>
                                        <IconButton size="small"
                                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); applyNumCols(n); }}
                                            sx={{ borderRadius: 0.5, minWidth: 28, bgcolor: numCols === n ? alpha(theme.palette.primary.main, 0.12) : 'transparent', color: numCols === n ? 'primary.main' : 'inherit' }}>
                                            <Typography variant="caption" fontWeight={700}>{n}</Typography>
                                        </IconButton>
                                    </Tooltip>
                                ))}
                                <Divider orientation="vertical" flexItem />
                                <Tooltip title="Column gap">
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.5 }} onMouseDown={(e) => e.stopPropagation()}>
                                        <Typography variant="caption" color="text.secondary">Gap</Typography>
                                        <Slider size="small" min={0} max={6} step={1} value={colGap}
                                            onChange={(_, v) => handleUpdate({ colGap: v })}
                                            sx={{ width: 60, color: 'primary.main', py: '6px' }} />
                                        <Typography variant="caption" color="text.secondary">{colGap}</Typography>
                                    </Stack>
                                </Tooltip>
                            </Stack>
                        )}

                        {/* Column grid */}
                        <Grid container spacing={colGap}>
                            {colDefs.map((col) => (
                                <Grid item xs={12} md={colWidth} key={col.id}>
                                    <Box
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            minHeight: 60,
                                            borderRadius: 1,
                                            border: isSiteBuilder ? '1px dashed' : 'none',
                                            borderColor: alpha(theme.palette.divider, 0.35),
                                            p: isSiteBuilder ? 1 : 0,
                                            minWidth: 0,
                                            transition: 'border-color 0.2s',
                                            '&:hover': isSiteBuilder ? { borderColor: alpha(theme.palette.primary.main, 0.4) } : {},
                                        }}
                                    >
                                        <FlexibleEditor
                                            blocks={col.blocks || []}
                                            onChange={(updated) => updateColumn(col.id, updated)}
                                            isSiteBuilder={isSiteBuilder}
                                            disableBlockActions={false}
                                            hideBlockDelete={false}
                                            hidePrompt={true}
                                            nestedDelete={true}
                                            allowEmpty={true}
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );
            }

            case 'customcomponent':
                return (
                    <Box sx={{ position: 'relative' }}>
                        {isSiteBuilder && isActive && isFormCustomInline && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    position: 'absolute',
                                    top: -44,
                                    right: 0,
                                    zIndex: 10,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 0.5,
                                    boxShadow: 1,
                                }}
                            >
                                <Tooltip title="Edit form">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openFormEditorFromFlexibleBlock();
                                        }}
                                    >
                                        <EditRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                {typeof onDuplicate === 'function' && (
                                    <Tooltip title="Duplicate">
                                        <IconButton
                                            size="small"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDuplicate(block.id);
                                            }}
                                        >
                                            <ContentCopyRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {typeof onDelete === 'function' && (
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(block.id);
                                            }}
                                        >
                                            <DeleteRounded sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        )}
                        <Box
                            sx={{
                                borderRadius: 1,
                                border: isSiteBuilder ? '1px dashed' : 'none',
                                borderColor: alpha(theme.palette.divider, 0.25),
                                p: isSiteBuilder ? 1 : 0,
                            }}
                        >
                            <CustomComponent
                                item={block.customItem || { type: block.variant || 'typography' }}
                                onUpdate={(updatedItem) => handleUpdate({ customItem: { ...updatedItem } })}
                                onDeleteBlock={() => onDelete && onDelete(block.id)}
                                onDuplicateBlock={() => onDuplicate && onDuplicate(block.id)}
                            />
                        </Box>
                    </Box>
                );

            case 'accordion': {
                const accordionEmoji = block.emoji || '✨';
                const accordionBlocks = block.blocks || [];
                const _stripHtml = (v) =>
                    typeof v === 'string'
                        ? v.replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim()
                        : '';
                const previewText = (block.blocks || [])
                    .map((b) => _stripHtml(b.value))
                    .filter((v) => v.length > 0)
                    .join(' · ');

                return (
                    <Box
                        sx={{
                            mb: 2,
                            position: 'relative',
                            bgcolor: alpha(theme.palette.background.paper, 0.4),
                            border: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.1),
                            borderRadius: '4px',
                            p: { xs: 2, md: 3 },
                            cursor: isSiteBuilder && !expandedAccordion ? 'pointer' : 'default',
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                boxShadow: '0 8px 16px rgba(0,0,0,0.05)',
                                '& .accordion-hover-actions': { opacity: expandedAccordion ? 0 : 1 },
                            }
                        }}
                        onClick={(e) => { if (!expandedAccordion) { e.stopPropagation(); setExpandedAccordion(true); } }}
                    >
                        {/* Hover actions: Show/Hide preview + Expand — visible when collapsed */}
                        {isSiteBuilder && !expandedAccordion && (
                            <Stack
                                className="accordion-hover-actions"
                                direction="row"
                                spacing={1}
                                sx={{ position: 'absolute', top: 12, right: 12, opacity: 0, transition: '0.2s', zIndex: 5 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setAccordionHiddenPreview((prev) => !prev)}
                                    sx={{ bgcolor: 'background.paper', color: 'text.primary', textTransform: 'none', fontWeight: 500, boxShadow: 1, '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                    {accordionHiddenPreview ? 'Show preview' : 'Hide preview'}
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<OpenInFullRounded sx={{ fontSize: '14px !important' }} />}
                                    onClick={() => setExpandedAccordion(true)}
                                    sx={{ bgcolor: 'background.paper', color: 'text.primary', textTransform: 'none', fontWeight: 500, boxShadow: 1, '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                    Expand
                                </Button>
                            </Stack>
                        )}

                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            {/* Emoji */}
                            <Box
                                contentEditable={isSiteBuilder}
                                suppressContentEditableWarning
                                onBlur={(e) => handleUpdate({ emoji: e.currentTarget.innerText })}
                                onClick={(e) => { if (isSiteBuilder) e.stopPropagation(); }}
                                sx={{ fontSize: '1.5rem', pt: 0.5, outline: 'none', minWidth: 32, userSelect: isSiteBuilder ? 'text' : 'none' }}
                            >
                                {accordionEmoji}
                            </Box>

                            <Stack spacing={1} flex={1} minWidth={0}>
                                {/* Title */}
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    contentEditable={isSiteBuilder}
                                    suppressContentEditableWarning
                                    onBlur={(e) => handleUpdate({ title: e.currentTarget.innerText })}
                                    onClick={(e) => { if (isSiteBuilder) e.stopPropagation(); }}
                                    sx={{ outline: 'none', lineHeight: 1.2, mb: 0.5, color: 'text.primary', ...pageFontSx }}
                                >
                                    {block.title || 'New Category'}
                                </Typography>

                                {/* Collapsed preview: full text OR "View more" depending on hidden state */}
                                {!expandedAccordion && (
                                    accordionHiddenPreview ? (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.82rem',
                                                color: 'text.secondary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.4,
                                                cursor: 'default',
                                                userSelect: 'none',
                                                width: 'fit-content',
                                            }}
                                        >
                                            View more
                                            <Iconify icon="eva:chevron-down-fill" sx={{ width: 14, height: 14 }} />
                                        </Typography>
                                    ) : (
                                        previewText ? (
                                            <Typography
                                                className="accordion-preview-text"
                                                variant="body2"
                                                color="text.secondary"
                                                noWrap
                                                sx={{
                                                    lineHeight: 1.4,
                                                    fontSize: '0.85rem',
                                                    pointerEvents: 'none',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {previewText}
                                            </Typography>
                                        ) : null
                                    )
                                )}

                                {/* Expanded: full block editor */}
                                {expandedAccordion && (
                                    <Stack spacing={0} onClick={(e) => e.stopPropagation()}>
                                        <FlexibleEditor
                                            blocks={accordionBlocks}
                                            onChange={(updated) => handleUpdate({ blocks: updated })}
                                            isSiteBuilder={isSiteBuilder}
                                            disableBlockActions={true}
                                        />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); setExpandedAccordion(false); }}
                                                endIcon={<Iconify icon="eva:chevron-up-fill" />}
                                                sx={{ textTransform: 'none', color: 'text.secondary' }}
                                            >
                                                Collapse
                                            </Button>
                                        </Box>
                                    </Stack>
                                )}
                            </Stack>

                            {/* Expand icon (right side) — only expands, never collapses on click */}
                            {!expandedAccordion && (
                                <Box
                                    onClick={(e) => { e.stopPropagation(); setExpandedAccordion(true); }}
                                    sx={{
                                        color: alpha(theme.palette.text.primary, 0.25),
                                        pt: 0.5,
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    <OpenInFullRounded sx={{ fontSize: 18 }} />
                                </Box>
                            )}
                        </Stack>
                    </Box>
                );
            }

            default:
                const isQuote = block.blockStyle === 'quote';
                const isCode = block.blockStyle === 'code';
                const quoteLayout = block.quoteLayout || 'classic';
                const isBubbleQuote = isQuote && quoteLayout === 'bubble';
                const isClassicQuote = isQuote && quoteLayout === 'classic';
                const quoteAccentColor = block.quoteAccentColor || theme.palette.primary.main;
                const quoteTextColor = block.quoteTextColor || (isBubbleQuote ? theme.palette.primary.contrastText : theme.palette.text.primary);
                // Grid / list display mode (quote blocks only)
                const quoteMode = isQuote ? (block.quoteMode || 'list') : 'list';
                const quoteGridCols = isQuote ? (block.quoteGridCols || 3) : 3;
                const isQuoteGrid = isQuote && quoteMode === 'grid';
                // Multi-item support — fall back to a virtual single-item for legacy blocks
                const quoteItems = block.quoteItems || [{ id: String(block.id) + '_0', value: block.value || '' }];
                const handleUpdateQuoteItem = (itemId, newHtml) => {
                    const updated = quoteItems.map((it) => String(it.id) === String(itemId) ? { ...it, value: newHtml } : it);
                    handleUpdate({ quoteItems: updated });
                };
                const handleAddQuoteItem = () => {
                    const newItem = { id: Date.now(), value: '<div class="quote-title">Item</div><div class="quote-description">Description</div>' };
                    handleUpdate({ quoteItems: [...quoteItems, newItem] });
                };
                const handleDeleteQuoteItem = (itemId) => {
                    if (quoteItems.length <= 1) return;
                    handleUpdate({ quoteItems: quoteItems.filter((it) => String(it.id) !== String(itemId)) });
                };

                return (
                    <Box sx={{ position: 'relative' }}>
                        {isSiteBuilder && isActive && isQuote && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                    position: 'absolute',
                                    top: -44,
                                    right: 0,
                                    zIndex: 10,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 0.5,
                                    boxShadow: 2,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Layout */}
                                <Tooltip title="Classic Quote">
                                    <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdate({ quoteLayout: 'classic' }); }}
                                        sx={{ bgcolor: quoteLayout === 'classic' ? alpha(theme.palette.primary.main, 0.12) : 'transparent', color: quoteLayout === 'classic' ? 'primary.main' : 'inherit', borderRadius: 0.5 }}>
                                        <FormatQuoteRounded fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Quote Bubble">
                                    <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdate({ quoteLayout: 'bubble' }); }}
                                        sx={{ bgcolor: quoteLayout === 'bubble' ? alpha(theme.palette.primary.main, 0.12) : 'transparent', color: quoteLayout === 'bubble' ? 'primary.main' : 'inherit', borderRadius: 0.5 }}>
                                        <ChatBubbleOutlineRounded fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Divider orientation="vertical" flexItem />

                                {/* List / Grid */}
                                <Tooltip title="List view">
                                    <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdate({ quoteMode: 'list' }); }}
                                        sx={{ bgcolor: quoteMode === 'list' ? alpha(theme.palette.primary.main, 0.12) : 'transparent', color: quoteMode === 'list' ? 'primary.main' : 'inherit', borderRadius: 0.5 }}>
                                        <ViewAgendaRounded fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Grid view">
                                    <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdate({ quoteMode: 'grid' }); }}
                                        sx={{ bgcolor: quoteMode === 'grid' ? alpha(theme.palette.primary.main, 0.12) : 'transparent', color: quoteMode === 'grid' ? 'primary.main' : 'inherit', borderRadius: 0.5 }}>
                                        <ViewModuleRounded fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {/* Column count — only when grid */}
                                {isQuoteGrid && (
                                    <>
                                        <Divider orientation="vertical" flexItem />
                                        <ToggleButtonGroup size="small" exclusive value={String(quoteGridCols)}
                                            onChange={(e, next) => next && handleUpdate({ quoteGridCols: Number(next) })}>
                                            <ToggleButton value="2" sx={{ px: 1.5, minWidth: 30 }}>2</ToggleButton>
                                            <ToggleButton value="3" sx={{ px: 1.5, minWidth: 30 }}>3</ToggleButton>
                                            <ToggleButton value="4" sx={{ px: 1.5, minWidth: 30 }}>4</ToggleButton>
                                        </ToggleButtonGroup>
                                    </>
                                )}

                                <Divider orientation="vertical" flexItem />

                                {/* Accent colour */}
                                <Tooltip title="Accent Color">
                                    <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setQuoteColorTarget('accent'); setQuoteColorAnchor(e.currentTarget); }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                            <FormatColorFillRounded fontSize="small" />
                                            <Box sx={{ width: 14, height: 3, borderRadius: '2px', bgcolor: quoteAccentColor }} />
                                        </Box>
                                    </IconButton>
                                </Tooltip>

                                {/* Colour picker popover */}
                                <Popover
                                    open={Boolean(quoteColorAnchor)}
                                    anchorEl={quoteColorAnchor}
                                    onClose={() => setQuoteColorAnchor(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ zIndex: 9999 }}
                                >
                                    <Stack spacing={1} sx={{ p: 1.5, minWidth: 170 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Accent Color</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <input type="color" value={quoteAccentColor}
                                                onChange={(e) => handleUpdate({ quoteAccentColor: e.target.value })}
                                                style={{ width: 36, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer', padding: 2 }} />
                                            <InputBase value={quoteAccentColor}
                                                onChange={(e) => handleUpdate({ quoteAccentColor: e.target.value })}
                                                inputProps={{ maxLength: 7, style: { fontFamily: 'monospace', fontSize: 13 } }}
                                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, flex: 1, height: 36 }} />
                                        </Stack>
                                    </Stack>
                                </Popover>
                            </Stack>
                        )}


                        {/* ── Non-quote blocks (plain text, code) ── */}
                        {!isQuote && (
                            <Typography
                                id={`block-${block.id}`}
                                component="div"
                                variant={block.variant || 'body1'}
                                contentEditable={isSiteBuilder}
                                suppressContentEditableWarning
                                dangerouslySetInnerHTML={{ __html: block.value || '' }}
                                onBlur={(e) => {
                                    if (e.relatedTarget && containerRef.current?.contains(e.relatedTarget)) return;
                                    handleUpdate(e.currentTarget.innerHTML);
                                }}
                                onKeyDown={(e) => onKeyDown(e, block)}
                                sx={{
                                    outline: 'none',
                                    cursor: isSiteBuilder ? 'text' : 'inherit',
                                    px: isCode ? 2 : 1,
                                    py: isCode ? 1.5 : '4px',
                                    minHeight: '1.5em',
                                    borderRadius: 1,
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.5,
                                    border: 'none',
                                    borderBottom: (isSiteBuilder && isActive && !isCode) ? `1px dashed ${alpha(theme.palette.primary.main, 0.4)}` : '1px solid transparent',
                                    ...(!isCode ? pageFontSx : {}),
                                    ...(isCode && {
                                        bgcolor: 'action.hover',
                                        fontFamily: 'monospace',
                                        fontSize: '0.9rem',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }),
                                    '& a': {
                                        color: 'inherit',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    },
                                    '&:empty:before': isSiteBuilder ? {
                                        content: '"Type text here..."',
                                        color: alpha(theme.palette.text.primary, 0.3),
                                        pointerEvents: 'none',
                                        display: 'block'
                                    } : {}
                                }}
                            />
                        )}

                        {/* ── Quote blocks — multi-item list / grid ── */}
                        {isQuote && (() => {
                            // Shared sx builder for a single quote Typography
                            const quoteItemSx = (isGridItem) => ({
                                ...pageFontSx,
                                outline: 'none',
                                cursor: isSiteBuilder ? 'text' : 'inherit',
                                minHeight: '1.5em',
                                borderRadius: 1,
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.5,
                                border: '1px solid transparent',
                                '& a': {
                                    color: 'inherit',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                },
                                '&:hover': isSiteBuilder ? { bgcolor: isClassicQuote ? alpha(theme.palette.action.hover, 0.05) : quoteAccentColor } : {},
                                ...(isClassicQuote && {
                                    position: 'relative',
                                    width: isGridItem ? 'calc(100% - 32px)' : { xs: 'calc(100% - 48px)', md: 'calc(100% - 72px)' },
                                    ml: isGridItem ? 2 : { xs: 3, md: 4 },
                                    mr: 'auto',
                                    my: 1.5,
                                    px: { xs: 2, md: 3 },
                                    py: 2,
                                    textAlign: 'center',
                                    border: `2px solid ${quoteAccentColor}`,
                                    borderRadius: 0,
                                    color: quoteTextColor,
                                    fontStyle: 'normal',
                                    backgroundColor: 'transparent',
                                    lineHeight: 1.7,
                                    '& .quote-title': { fontSize: { xs: '1.4rem', md: isGridItem ? '1.5rem' : '2.1rem' }, lineHeight: 1.2, marginBottom: '8px' },
                                    '& .quote-description': { fontSize: { xs: '0.9rem', md: isGridItem ? '0.95rem' : '1.15rem' }, lineHeight: 1.5 },
                                    '&:before': { content: '"\\201C"', position: 'absolute', left: -4, top: -18, color: quoteAccentColor, fontSize: '2.6rem', lineHeight: 1, backgroundColor: theme.palette.background.default, px: 0.25 },
                                    '&:after': { content: '"\\201D"', position: 'absolute', right: -4, bottom: -18, color: quoteAccentColor, fontSize: '2.6rem', lineHeight: 1, backgroundColor: theme.palette.background.default, px: 0.25 }
                                }),
                                ...(isBubbleQuote && {
                                    position: 'relative',
                                    zIndex: 1,
                                    width: isGridItem ? '100%' : { xs: '96%', md: '92%' },
                                    mx: isGridItem ? 0 : 'auto',
                                    mt: 1.5,
                                    mb: isGridItem ? 4 : 5,
                                    px: { xs: 2, md: 3 },
                                    py: 2.5,
                                    textAlign: 'left',
                                    bgcolor: quoteAccentColor,
                                    color: quoteTextColor,
                                    borderRadius: '12px',
                                    border: 'none',
                                    '&:hover': isSiteBuilder ? { bgcolor: quoteAccentColor } : {},
                                    '& .quote-title': { fontSize: { xs: '1.2rem', md: isGridItem ? '1.3rem' : '1.6rem' }, fontWeight: 600, lineHeight: 1.3, marginBottom: '6px' },
                                    '& .quote-description': { fontSize: { xs: '0.85rem', md: isGridItem ? '0.9rem' : '1rem' }, lineHeight: 1.5, opacity: 0.95 },
                                    '&:after': {
                                        content: '""', position: 'absolute', bottom: -24, right: '15%',
                                        width: 0, height: 0,
                                        borderLeft: '14px solid transparent',
                                        borderRight: '14px solid transparent',
                                        borderTop: `24px solid ${quoteAccentColor}`
                                    }
                                }),
                            });

                            const renderItem = (item, idx) => {
                                const isGrid = isQuoteGrid;
                                return (
                                    <Box
                                        key={item.id}
                                        sx={{ position: 'relative', '&:hover .quote-delete-btn': { opacity: 1 } }}
                                    >
                                        {isSiteBuilder && isActive && quoteItems.length > 1 && (
                                            <IconButton
                                                className="quote-delete-btn"
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteQuoteItem(item.id)}
                                                sx={{ position: 'absolute', top: isGrid ? 8 : (isClassicQuote ? 16 : 8), right: isGrid ? 4 : (isClassicQuote ? 4 : 12), zIndex: 3, opacity: 0, transition: 'opacity 0.15s' }}
                                            >
                                                <DeleteRounded fontSize="small" />
                                            </IconButton>
                                        )}
                                        <Typography
                                            id={idx === 0 ? `block-${block.id}` : undefined}
                                            component="div"
                                            variant={block.variant || 'body1'}
                                            contentEditable={isSiteBuilder}
                                            suppressContentEditableWarning
                                            dangerouslySetInnerHTML={{ __html: formatClassicQuoteHtml(item.value || '') }}
                                            onBlur={(e) => {
                                                if (e.relatedTarget && containerRef.current?.contains(e.relatedTarget)) return;
                                                handleUpdateQuoteItem(item.id, e.currentTarget.innerHTML);
                                            }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.execCommand('insertLineBreak'); } }}
                                            sx={quoteItemSx(isGrid)}
                                        />
                                    </Box>
                                );
                            };

                            return (
                                <>
                                    {isQuoteGrid ? (
                                        <Grid container spacing={isClassicQuote ? 5 : 2} sx={{ mt: 0 }}>
                                            {quoteItems.map((item, idx) => (
                                                <Grid item xs={12} sm={Math.max(6, 12 / quoteGridCols)} md={12 / quoteGridCols} key={item.id}>
                                                    {renderItem(item, idx)}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Box>
                                            {quoteItems.map((item, idx) => renderItem(item, idx))}
                                        </Box>
                                    )}
                                    {isSiteBuilder && isActive && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                            <Button
                                                size="small"
                                                startIcon={<AddRounded />}
                                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleAddQuoteItem(); }}
                                                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
                                            >
                                                Add Quote
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                );
        }
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                mb: 1.5,
                '&:hover .block-actions': { opacity: 1 },
                '&:hover .grid-variant-toolbar': { opacity: 1 },
                ...(isSiteBuilder && isActuallyActive && {
                    outline: `1px solid ${theme.palette.info.main}`,
                    outlineOffset: '-1px'
                })
            }}
            className="relative"
            onMouseUp={handleMouseUp}
        >
            {isSiteBuilder && toolbarEnabledTypes.includes(block.type) && (selectionRect || lastSelectionRect || toolbarLinkMode) && (
                <Box
                    key={block.id + "_text_toolbar"}
                    data-floating-text-toolbar="true"
                    onMouseEnter={() => { isMouseOverToolbarRef.current = true; }}
                    onMouseLeave={() => { isMouseOverToolbarRef.current = false; }}
                    onMouseDown={(e) => { e.stopPropagation(); isMouseOverToolbarRef.current = true; }}
                    onMouseUp={(e) => { e.stopPropagation(); isMouseOverToolbarRef.current = true; }}
                    onClick={(e) => { e.stopPropagation(); isMouseOverToolbarRef.current = true; }}
                >
                    <FloatingTextToolbar
                        selectionRect={selectionRect || lastSelectionRect || toolbarFallbackRect}
                        absolute={false}
                        linkMode={toolbarLinkMode}
                        setLinkMode={setToolbarLinkMode}
                        url={toolbarUrl}
                        setUrl={setToolbarUrl}
                    />
                </Box>
            )}
            {isSiteBuilder && isActuallyActive && ['table', 'statgrid', 'testimonialgrid'].includes(block.type) && (
                <Box
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        mb: 1
                    }}
                >
                    <TextPropertiesPanel
                        childOverride={block}
                        onChangeChild={(updated) => handleUpdate(updated)}
                        onDeleteChild={() => onDelete && onDelete(block.id)}
                        onDuplicateChild={() => onDuplicate && onDuplicate(block.id)}
                    />
                </Box>
            )}
            {renderBlockActions()}
            <Box>
                {renderContent()}
            </Box>
        </Box>
    );
};

export default CommonBlock;
